import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  List,
  ListOrdered,
  Quote,
  Minus,
  Undo,
  Redo,
  Link as LinkIcon,
  Image as ImageIcon,
  Table as TableIcon,
  Palette,
  Highlighter,
  Heading1,
  Heading2,
  Heading3,
  Type
} from 'lucide-react';

// Custom Image extension that allows base64 data URIs
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
        parseHTML: element => element.getAttribute('src'),
        renderHTML: attributes => {
          if (!attributes['src']) {
            return {};
          }
          return {
            src: attributes['src'],
          };
        },
      },
    };
  },
  
  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: element => {
          const src = (element as HTMLElement).getAttribute('src');
          // Allow all image sources including data URIs
          return src ? {} : false;
        },
      },
    ];
  },
});

const TEXT_COLORS = ['#000000', '#ef4444', '#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#6b7280'];
const HIGHLIGHT_COLORS = ['#fef3c7', '#fecaca', '#bbf7d0', '#dbeafe', '#e0e7ff', '#f3e8ff', '#fce7f3', '#f3f4f6'];
const SWATCH_SIZE_PX = 24;
const SWATCH_GAP_PX = 6;

const colorPaletteGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: `repeat(4, ${SWATCH_SIZE_PX}px)`,
  gap: SWATCH_GAP_PX,
};

const colorPalettePanelStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  border: '1px solid var(--color-border)',
  boxShadow: 'var(--shadow-lg)',
  width: SWATCH_SIZE_PX * 4 + SWATCH_GAP_PX * 3 + 16,
};

type ColorPaletteId = 'text-color' | 'highlight';

interface ColorPaletteDropdownProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  icon: React.ReactNode;
  title: string;
  colors: string[];
  onSelect: (color: string) => void;
  swatchTitle: (color: string) => string;
  onClear?: () => void;
  clearLabel?: string;
}

const ColorPaletteDropdown: React.FC<ColorPaletteDropdownProps> = ({
  isOpen,
  onToggle,
  onClose,
  icon,
  title,
  colors,
  onSelect,
  swatchTitle,
  onClear,
  clearLabel = 'Remove',
}) => {
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="p-1.5 rounded transition-colors"
        style={{
          backgroundColor: isOpen ? 'var(--color-accent)' : 'transparent',
          color: isOpen ? 'white' : 'var(--color-text-secondary)',
        }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--color-text-secondary)';
          }
        }}
        title={title}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {icon}
      </button>
      {isOpen && (
        <div
          className="absolute top-full left-0 pt-1 z-50"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="rounded-lg p-2" style={colorPalettePanelStyle}>
            <div style={colorPaletteGridStyle}>
              {colors.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(color);
                    onClose();
                  }}
                  title={swatchTitle(color)}
                  style={{
                    width: SWATCH_SIZE_PX,
                    height: SWATCH_SIZE_PX,
                    minWidth: SWATCH_SIZE_PX,
                    minHeight: SWATCH_SIZE_PX,
                    flexShrink: 0,
                    padding: 0,
                    backgroundColor: color,
                    border: '1px solid var(--color-border)',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                />
              ))}
            </div>
            {onClear && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                  onClose();
                }}
                className="mt-2 w-full rounded px-2 py-1 text-xs font-medium transition-colors"
                style={{
                  color: 'var(--color-text-secondary)',
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                  e.currentTarget.style.color = 'var(--color-text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                  e.currentTarget.style.color = 'var(--color-text-secondary)';
                }}
              >
                {clearLabel}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface TiptapEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
  editorMinHeight?: string;
}

export const TiptapEditor: React.FC<TiptapEditorProps> = ({
  content,
  onChange,
  placeholder = 'Start writing...',
  className = '',
  editorMinHeight = '200px',
}) => {
  // @ts-ignore - Tiptap version mismatch between starter-kit and core packages
  const editor = useEditor({
    extensions: [
      // @ts-ignore - Tiptap version mismatch
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline cursor-pointer',
        },
      }),
      CustomImage.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onCreate: () => {
      console.log('TiptapEditor onCreate - Editor created');
      // Let the useEffect handle content setting for consistency
    },
    onFocus: ({ editor }) => {
      console.log('Editor focused:', editor);
    },
    onBlur: ({ editor }) => {
      console.log('Editor blurred:', editor);
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none p-3 tiptap',
        style: `
          min-height: ${editorMinHeight};
          color: var(--color-text-primary); 
          background-color: var(--color-bg-primary);
          font-family: inherit;
        `,
      },
      handleDOMEvents: {
        keydown: (_view, event) => {
          // Prevent parent <form> from treating Enter as implicit submit
          if (event.key === 'Enter') {
            event.stopPropagation();
          }
          return false;
        },
      },
    },
  }); // Remove content dependency to prevent excessive recreation

  // Update editor content when content prop changes
  useEffect(() => {
    if (editor && content !== undefined && content !== null) {
      const currentContent = editor.getHTML();
      
      if (content !== currentContent) {
        try {
          // Use our custom Image extension to preserve base64 images
          editor.commands.setContent(content, { emitUpdate: false });
        } catch (error) {
          console.error('TiptapEditor: Error setting content:', error);
        }
      }
    }
  }, [editor, content]);

  // Inject custom CSS for Tiptap editor styling
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Dark theme scrollbar styling */
      .dark-scrollbar::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      
      .dark-scrollbar::-webkit-scrollbar-track {
        background: var(--color-bg-tertiary, #1a1a1a);
        border-radius: 4px;
      }
      
      .dark-scrollbar::-webkit-scrollbar-thumb {
        background: var(--color-border, #374151);
        border-radius: 4px;
        border: 1px solid var(--color-bg-tertiary, #1a1a1a);
      }
      
      .dark-scrollbar::-webkit-scrollbar-thumb:hover {
        background: var(--color-text-muted, #6b7280);
      }
      
      .dark-scrollbar::-webkit-scrollbar-corner {
        background: var(--color-bg-tertiary, #1a1a1a);
      }
      
      /* Firefox dark scrollbar */
      .dark-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: var(--color-border, #374151) var(--color-bg-tertiary, #1a1a1a);
      }
      .tiptap h1 {
        font-size: 2.25rem !important;
        line-height: 2.5rem !important;
        font-weight: 700 !important;
        margin-top: 1.5rem !important;
        margin-bottom: 1rem !important;
        color: var(--color-text-primary) !important;
        display: block !important;
      }
      .tiptap h2 {
        font-size: 1.875rem !important;
        line-height: 2.25rem !important;
        font-weight: 600 !important;
        margin-top: 1.25rem !important;
        margin-bottom: 0.75rem !important;
        color: var(--color-text-primary) !important;
        display: block !important;
      }
      .tiptap h3 {
        font-size: 1.5rem !important;
        line-height: 2rem !important;
        font-weight: 600 !important;
        margin-top: 1rem !important;
        margin-bottom: 0.5rem !important;
        color: var(--color-text-primary) !important;
        display: block !important;
      }
      .tiptap ul {
        list-style-type: disc !important;
        margin-top: 0.75rem !important;
        margin-bottom: 0.75rem !important;
        padding-left: 1.5rem !important;
        display: block !important;
      }
      .tiptap ol {
        list-style-type: decimal !important;
        margin-top: 0.75rem !important;
        margin-bottom: 0.75rem !important;
        padding-left: 1.5rem !important;
        display: block !important;
      }
      .tiptap li {
        margin-top: 0.25rem !important;
        margin-bottom: 0.25rem !important;
        color: var(--color-text-primary) !important;
        display: list-item !important;
      }
      .tiptap blockquote {
        border-left: 4px solid var(--color-border) !important;
        padding-left: 1rem !important;
        margin-top: 1rem !important;
        margin-bottom: 1rem !important;
        font-style: italic !important;
        color: var(--color-text-secondary) !important;
        display: block !important;
      }
      .tiptap p {
        margin-top: 0.75rem !important;
        margin-bottom: 0.75rem !important;
        color: var(--color-text-primary) !important;
        display: block !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (!editor) {
    return null;
  }

  const addLink = () => {
    // Create a modal-like interface for link input
    const container = document.createElement('div');
    container.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]';
    container.style.position = 'fixed';
    
    const modal = document.createElement('div');
    modal.className = 'bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4';
    modal.style.backgroundColor = 'var(--color-bg-primary)';
    modal.style.color = 'var(--color-text-primary)';
    modal.style.border = '1px solid var(--color-border)';
    
    modal.innerHTML = `
      <h3 class="text-lg font-semibold mb-4">Add Link</h3>
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium mb-2">URL</label>
          <input type="url" id="linkUrl" placeholder="https://example.com" 
                 class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                 style="background-color: var(--color-bg-secondary); border-color: var(--color-border); color: var(--color-text-primary);">
        </div>
        <div>
          <label class="block text-sm font-medium mb-2">Link Text (optional)</label>
          <input type="text" id="linkText" placeholder="Link text" 
                 class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                 style="background-color: var(--color-bg-secondary); border-color: var(--color-border); color: var(--color-text-primary);">
        </div>
      </div>
      <div class="flex justify-end space-x-3 mt-6">
        <button id="cancelBtn" class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">Cancel</button>
        <button id="addBtn" class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Add Link</button>
      </div>
    `;
    
    container.appendChild(modal);
    document.body.appendChild(container);
    
    const urlInput = modal.querySelector('#linkUrl') as HTMLInputElement;
    const textInput = modal.querySelector('#linkText') as HTMLInputElement;
    const cancelBtn = modal.querySelector('#cancelBtn') as HTMLButtonElement;
    const addBtn = modal.querySelector('#addBtn') as HTMLButtonElement;
    
    const handleAdd = () => {
      const url = urlInput.value.trim();
      const text = textInput.value.trim();
      
      if (url) {
        if (text) {
          // Insert link with custom text
          editor.chain().focus().insertContent(`<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`).run();
        } else {
          // Insert link at current selection
          editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }
        document.body.removeChild(container);
      }
    };
    
    const handleCancel = () => {
      document.body.removeChild(container);
    };
    
    cancelBtn.addEventListener('click', handleCancel);
    addBtn.addEventListener('click', handleAdd);
    
    // Close on backdrop click
    container.addEventListener('click', (e) => {
      if (e.target === container) {
        handleCancel();
      }
    });
    
    // Handle Enter key on URL input
    urlInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        handleAdd();
      }
    });
    
    // Focus URL input
    urlInput.focus();
  };

  const addImage = () => {
    // Create a modal-like interface for image input
    const container = document.createElement('div');
    container.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]';
    container.style.position = 'fixed';
    
    const modal = document.createElement('div');
    modal.className = 'bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4';
    modal.style.backgroundColor = 'var(--color-bg-primary)';
    modal.style.color = 'var(--color-text-primary)';
    modal.style.border = '1px solid var(--color-border)';
    
    modal.innerHTML = `
      <h3 class="text-lg font-semibold mb-4">Add Image</h3>
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium mb-2">Image URL</label>
          <input type="url" id="imageUrl" placeholder="https://example.com/image.jpg" 
                 class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                 style="background-color: var(--color-bg-secondary); border-color: var(--color-border); color: var(--color-text-primary);">
        </div>
        <div class="text-center">
          <span class="text-sm text-gray-500">OR</span>
        </div>
        <div>
          <label class="block text-sm font-medium mb-2">Upload Image</label>
          <input type="file" id="imageFile" accept="image/*" 
                 class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                 style="background-color: var(--color-bg-secondary); border-color: var(--color-border); color: var(--color-text-primary);">
        </div>
      </div>
      <div class="flex justify-end space-x-3 mt-6">
        <button id="cancelBtn" class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">Cancel</button>
        <button id="addBtn" class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Add Image</button>
      </div>
    `;
    
    container.appendChild(modal);
    document.body.appendChild(container);
    
    const urlInput = modal.querySelector('#imageUrl') as HTMLInputElement;
    const fileInput = modal.querySelector('#imageFile') as HTMLInputElement;
    const cancelBtn = modal.querySelector('#cancelBtn') as HTMLButtonElement;
    const addBtn = modal.querySelector('#addBtn') as HTMLButtonElement;
    
    let imageSrc = '';
    
    const handleAdd = () => {
      if (urlInput.value.trim()) {
        imageSrc = urlInput.value.trim();
      } else if (fileInput.files && fileInput.files[0]) {
        const file = fileInput.files[0];
        const reader = new FileReader();
        reader.onload = (e) => {
          imageSrc = e.target?.result as string;
          insertImage();
        };
        reader.readAsDataURL(file);
        return;
      }
      
      if (imageSrc) {
        insertImage();
      }
    };
    
    const insertImage = () => {
      editor.chain().focus().setImage({ src: imageSrc }).run();
      document.body.removeChild(container);
    };
    
    const handleCancel = () => {
      document.body.removeChild(container);
    };
    
    cancelBtn.addEventListener('click', handleCancel);
    addBtn.addEventListener('click', handleAdd);
    
    // Close on backdrop click
    container.addEventListener('click', (e) => {
      if (e.target === container) {
        handleCancel();
      }
    });
    
    // Handle Enter key on URL input
    urlInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        handleAdd();
      }
    });
    
    // Focus URL input
    urlInput.focus();
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const setColor = (color: string) => {
    editor.chain().focus().setColor(color).run();
  };

  const clearColor = () => {
    editor.chain().focus().unsetColor().run();
  };

  const setHighlight = (color: string) => {
    editor.chain().focus().setHighlight({ color }).run();
  };

  const clearHighlight = () => {
    editor.chain().focus().unsetHighlight().run();
  };

  const [openPaletteId, setOpenPaletteId] = useState<ColorPaletteId | null>(null);
  const paletteGroupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openPaletteId) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (paletteGroupRef.current && !paletteGroupRef.current.contains(event.target as Node)) {
        setOpenPaletteId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openPaletteId]);

  const togglePalette = (id: ColorPaletteId) => {
    setOpenPaletteId((current) => (current === id ? null : id));
  };

  return (
    <div 
      className={`border rounded-lg ${className}`}
      style={{
        borderColor: 'var(--color-border)',
        backgroundColor: 'var(--color-bg-secondary)'
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onKeyUp={(e) => e.stopPropagation()}
      onKeyPress={(e) => e.stopPropagation()}
    >

      {/* Toolbar */}
      <div 
        className="border-b p-2 flex flex-wrap items-center gap-1 rounded-t-lg"
        style={{
          borderColor: 'var(--color-border)',
          backgroundColor: 'var(--color-bg-tertiary)'
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Text Formatting */}
        <div 
          className="flex items-center gap-1 pr-2 border-r"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              editor.chain().focus().toggleMark('bold').run();
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: editor.isActive('bold') ? 'var(--color-accent)' : 'transparent',
              color: editor.isActive('bold') ? 'white' : 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              if (!editor.isActive('bold')) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!editor.isActive('bold')) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }
            }}
            title="Bold"
          >
            <Bold className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              editor.chain().focus().toggleMark('italic').run();
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: editor.isActive('italic') ? 'var(--color-accent)' : 'transparent',
              color: editor.isActive('italic') ? 'white' : 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              if (!editor.isActive('italic')) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!editor.isActive('italic')) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }
            }}
            title="Italic"
          >
            <Italic className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              editor.chain().focus().toggleMark('strike').run();
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: editor.isActive('strike') ? 'var(--color-accent)' : 'transparent',
              color: editor.isActive('strike') ? 'white' : 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              if (!editor.isActive('strike')) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!editor.isActive('strike')) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }
            }}
            title="Strikethrough"
          >
            <Strikethrough className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              editor.chain().focus().toggleMark('code').run();
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: editor.isActive('code') ? 'var(--color-accent)' : 'transparent',
              color: editor.isActive('code') ? 'white' : 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              if (!editor.isActive('code')) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!editor.isActive('code')) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }
            }}
            title="Code"
          >
            <Code className="w-4 h-4" />
          </button>
        </div>

        {/* Headings */}
        <div 
          className="flex items-center gap-1 pr-2 border-r"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              editor.chain().focus().setNode('paragraph').run();
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: editor.isActive('paragraph') ? 'var(--color-accent)' : 'transparent',
              color: editor.isActive('paragraph') ? 'white' : 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              if (!editor.isActive('paragraph')) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!editor.isActive('paragraph')) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }
            }}
            title="Paragraph"
          >
            <Type className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // If no text is selected, insert a heading
              if (editor.state.selection.empty) {
                editor.chain().focus().insertContent('<h1>Heading 1</h1>').run();
              } else {
                // @ts-ignore - Tiptap heading level type
                editor.chain().focus().toggleNode('heading', 'heading', { level: 1 }).run();
              }
              // Visual feedback - briefly change button color
              e.currentTarget.style.backgroundColor = 'var(--color-accent)';
              e.currentTarget.style.color = 'white';
              setTimeout(() => {
                e.currentTarget.style.backgroundColor = editor.isActive('heading', { level: 1 }) ? 'var(--color-accent)' : 'transparent';
                e.currentTarget.style.color = editor.isActive('heading', { level: 1 }) ? 'white' : 'var(--color-text-secondary)';
              }, 200);
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: editor.isActive('heading', { level: 1 }) ? 'var(--color-accent)' : 'transparent',
              color: editor.isActive('heading', { level: 1 }) ? 'white' : 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              if (!editor.isActive('heading', { level: 1 })) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!editor.isActive('heading', { level: 1 })) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }
            }}
            title="Heading 1"
          >
            <Heading1 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // If no text is selected, insert a heading
              if (editor.state.selection.empty) {
                editor.chain().focus().insertContent('<h2>Heading 2</h2>').run();
              } else {
                // @ts-ignore - Tiptap heading level type
                editor.chain().focus().toggleNode('heading', 'heading', { level: 2 }).run();
              }
              // Visual feedback - briefly change button color
              e.currentTarget.style.backgroundColor = 'var(--color-accent)';
              e.currentTarget.style.color = 'white';
              setTimeout(() => {
                e.currentTarget.style.backgroundColor = editor.isActive('heading', { level: 2 }) ? 'var(--color-accent)' : 'transparent';
                e.currentTarget.style.color = editor.isActive('heading', { level: 2 }) ? 'white' : 'var(--color-text-secondary)';
              }, 200);
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: editor.isActive('heading', { level: 2 }) ? 'var(--color-accent)' : 'transparent',
              color: editor.isActive('heading', { level: 2 }) ? 'white' : 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              if (!editor.isActive('heading', { level: 2 })) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!editor.isActive('heading', { level: 2 })) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }
            }}
            title="Heading 2"
          >
            <Heading2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // If no text is selected, insert a heading
              if (editor.state.selection.empty) {
                editor.chain().focus().insertContent('<h3>Heading 3</h3>').run();
              } else {
                // @ts-ignore - Tiptap heading level type
                editor.chain().focus().toggleNode('heading', 'heading', { level: 3 }).run();
              }
              // Visual feedback - briefly change button color
              e.currentTarget.style.backgroundColor = 'var(--color-accent)';
              e.currentTarget.style.color = 'white';
              setTimeout(() => {
                e.currentTarget.style.backgroundColor = editor.isActive('heading', { level: 3 }) ? 'var(--color-accent)' : 'transparent';
                e.currentTarget.style.color = editor.isActive('heading', { level: 3 }) ? 'white' : 'var(--color-text-secondary)';
              }, 200);
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: editor.isActive('heading', { level: 3 }) ? 'var(--color-accent)' : 'transparent',
              color: editor.isActive('heading', { level: 3 }) ? 'white' : 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              if (!editor.isActive('heading', { level: 3 })) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!editor.isActive('heading', { level: 3 })) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }
            }}
            title="Heading 3"
          >
            <Heading3 className="w-4 h-4" />
          </button>
        </div>

        {/* Lists */}
        <div 
          className="flex items-center gap-1 pr-2 border-r"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // If no text is selected, insert a bullet list
              if (editor.state.selection.empty) {
                editor.chain().focus().insertContent('<ul><li>List item</li></ul>').run();
              } else {
                editor.chain().focus().toggleList('bulletList', 'listItem').run();
              }
              // Visual feedback - briefly change button color
              e.currentTarget.style.backgroundColor = 'var(--color-accent)';
              e.currentTarget.style.color = 'white';
              setTimeout(() => {
                e.currentTarget.style.backgroundColor = editor.isActive('bulletList') ? 'var(--color-accent)' : 'transparent';
                e.currentTarget.style.color = editor.isActive('bulletList') ? 'white' : 'var(--color-text-secondary)';
              }, 200);
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: editor.isActive('bulletList') ? 'var(--color-accent)' : 'transparent',
              color: editor.isActive('bulletList') ? 'white' : 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              if (!editor.isActive('bulletList')) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!editor.isActive('bulletList')) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }
            }}
            title="Bullet List"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // If no text is selected, insert a numbered list
              if (editor.state.selection.empty) {
                editor.chain().focus().insertContent('<ol><li>List item</li></ol>').run();
              } else {
                editor.chain().focus().toggleList('orderedList', 'listItem').run();
              }
              // Visual feedback - briefly change button color
              e.currentTarget.style.backgroundColor = 'var(--color-accent)';
              e.currentTarget.style.color = 'white';
              setTimeout(() => {
                e.currentTarget.style.backgroundColor = editor.isActive('orderedList') ? 'var(--color-accent)' : 'transparent';
                e.currentTarget.style.color = editor.isActive('orderedList') ? 'white' : 'var(--color-text-secondary)';
              }, 200);
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: editor.isActive('orderedList') ? 'var(--color-accent)' : 'transparent',
              color: editor.isActive('orderedList') ? 'white' : 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              if (!editor.isActive('orderedList')) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!editor.isActive('orderedList')) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }
            }}
            title="Numbered List"
          >
            <ListOrdered className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // If no text is selected, insert a blockquote
              if (editor.state.selection.empty) {
                editor.chain().focus().insertContent('<blockquote><p>Quote text</p></blockquote>').run();
              } else {
                editor.chain().focus().toggleNode('blockquote', 'blockquote').run();
              }
              // Visual feedback - briefly change button color
              e.currentTarget.style.backgroundColor = 'var(--color-accent)';
              e.currentTarget.style.color = 'white';
              setTimeout(() => {
                e.currentTarget.style.backgroundColor = editor.isActive('blockquote') ? 'var(--color-accent)' : 'transparent';
                e.currentTarget.style.color = editor.isActive('blockquote') ? 'white' : 'var(--color-text-secondary)';
              }, 200);
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: editor.isActive('blockquote') ? 'var(--color-accent)' : 'transparent',
              color: editor.isActive('blockquote') ? 'white' : 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              if (!editor.isActive('blockquote')) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!editor.isActive('blockquote')) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }
            }}
            title="Quote"
          >
            <Quote className="w-4 h-4" />
          </button>
        </div>

        {/* Colors & Highlighting */}
        <div
          ref={paletteGroupRef}
          className="flex items-center gap-1 pr-2 border-r"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <ColorPaletteDropdown
            isOpen={openPaletteId === 'text-color'}
            onToggle={() => togglePalette('text-color')}
            onClose={() => setOpenPaletteId(null)}
            icon={<Palette className="w-4 h-4" />}
            title="Text Color"
            colors={TEXT_COLORS}
            onSelect={setColor}
            onClear={clearColor}
            clearLabel="Remove text color"
            swatchTitle={(color) => `Text Color: ${color}`}
          />
          <ColorPaletteDropdown
            isOpen={openPaletteId === 'highlight'}
            onToggle={() => togglePalette('highlight')}
            onClose={() => setOpenPaletteId(null)}
            icon={<Highlighter className="w-4 h-4" />}
            title="Highlight"
            colors={HIGHLIGHT_COLORS}
            onSelect={setHighlight}
            onClear={clearHighlight}
            clearLabel="Remove highlight"
            swatchTitle={(color) => `Highlight: ${color}`}
          />
        </div>

        {/* Links & Media */}
        <div 
          className="flex items-center gap-1 pr-2 border-r"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              addLink();
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: editor.isActive('link') ? 'var(--color-accent)' : 'transparent',
              color: editor.isActive('link') ? 'white' : 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              if (!editor.isActive('link')) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!editor.isActive('link')) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }
            }}
            title="Add Link"
          >
            <LinkIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              addImage();
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
              e.currentTarget.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--color-text-secondary)';
            }}
            title="Add Image"
          >
            <ImageIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              insertTable();
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
              e.currentTarget.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--color-text-secondary)';
            }}
            title="Insert Table"
          >
            <TableIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Utility */}
        <div 
          className="flex items-center gap-1 pr-2 border-r"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              editor.chain().focus().insertContent('<hr>').run();
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
              e.currentTarget.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--color-text-secondary)';
            }}
            title="Horizontal Rule"
          >
            <Minus className="w-4 h-4" />
          </button>
        </div>

        {/* Sample Text */}
        <div className="flex items-center gap-1 pr-2 border-r" style={{ borderColor: 'var(--color-border)' }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              editor.chain().focus().insertContent('<p>This is sample text to test formatting. You can select this text and then use the formatting buttons.</p>').run();
            }}
            className="p-1.5 rounded transition-colors"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
              e.currentTarget.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--color-text-secondary)';
            }}
            title="Insert Sample Text"
          >
            <Type className="w-4 h-4" />
          </button>
        </div>

        {/* Undo/Redo */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // @ts-ignore - Tiptap undo/redo API
              editor.commands.undo();
            }}
            // @ts-ignore - Tiptap undo/redo API
            disabled={!editor.can().undo()}
            className="p-1.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              // @ts-ignore - Tiptap undo/redo API
              if (!editor.can().undo()) return;
              e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
              e.currentTarget.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(e) => {
              // @ts-ignore - Tiptap undo/redo API
              if (!editor.can().undo()) return;
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--color-text-secondary)';
            }}
            title="Undo"
          >
            <Undo className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // @ts-ignore - Tiptap undo/redo API
              editor.commands.redo();
            }}
            // @ts-ignore - Tiptap undo/redo API
            disabled={!editor.can().redo()}
            className="p-1.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary)'
            }}
            onMouseEnter={(e) => {
              // @ts-ignore - Tiptap undo/redo API
              if (!editor.can().redo()) return;
              e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
              e.currentTarget.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(e) => {
              // @ts-ignore - Tiptap undo/redo API
              if (!editor.can().redo()) return;
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--color-text-secondary)';
            }}
            title="Redo"
          >
            <Redo className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Editor Content */}
      <div 
        className="relative"
        onClick={(e) => {
          e.stopPropagation();
          editor.commands.focus();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <EditorContent 
          editor={editor} 
          className="focus:outline-none"
          style={{
            // Custom CSS for proper element styling
            '--tw-prose-headings': 'var(--color-text-primary)',
            '--tw-prose-body': 'var(--color-text-primary)',
            '--tw-prose-links': 'var(--color-accent)',
            '--tw-prose-bold': 'var(--color-text-primary)',
            '--tw-prose-counters': 'var(--color-text-secondary)',
            '--tw-prose-bullets': 'var(--color-text-secondary)',
            '--tw-prose-hr': 'var(--color-border)',
            '--tw-prose-quotes': 'var(--color-text-primary)',
            '--tw-prose-quote-borders': 'var(--color-border)',
            '--tw-prose-captions': 'var(--color-text-secondary)',
            '--tw-prose-code': 'var(--color-text-primary)',
            '--tw-prose-pre-code': 'var(--color-text-primary)',
            '--tw-prose-pre-bg': 'var(--color-bg-secondary)',
            '--tw-prose-th-borders': 'var(--color-border)',
            '--tw-prose-td-borders': 'var(--color-border)',
          } as any}
        />
        {editor.isEmpty && (
          <p 
            className="absolute top-3 left-3 pointer-events-none"
            style={{ color: 'var(--color-text-muted)' }}
          >
            {placeholder}
          </p>
        )}
      </div>
    </div>
  );
};
